import { endOfDay, isAfter } from 'date-fns';
import { DateRange } from 'shared/types/date-range';
import { ID } from 'shared/types/id';
import {
  OrderDirection,
  priorityRanking,
  SortingMode,
} from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { TaskPriorityOptions } from 'shared/types/task-base';
import { Timeslot } from 'shared/types/timeslot';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import {
  getDueTaskPlanning,
  getTaskPlanning,
} from 'shared/utils/get-task-planning';
import { isNumber } from 'shared/utils/is-number';

import { getTaskDueDate } from './get-task-due';

type TaskSortGeneratorOptions = {
  invertedTimeslots?: boolean;
  allDayFirst?: boolean;
};

const taskSortGenerator =
  ({ invertedTimeslots, allDayFirst }: TaskSortGeneratorOptions = {}) =>
  (
    taskA: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    },
    taskB: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    },
  ) =>
    !!taskA.sortDate && !!taskB.sortDate
      ? taskA.sortDate.getTime() - taskB.sortDate.getTime() === 0
        ? isNumber(taskA.timeslot?.startTime) &&
          isNumber(taskB.timeslot?.startTime)
          ? invertedTimeslots
            ? taskB.timeslot!.startTime - taskA.timeslot!.startTime
            : taskA.timeslot!.startTime - taskB.timeslot!.startTime
          : isNumber(taskA.timeslot?.startTime)
            ? allDayFirst
              ? 1
              : -1
            : isNumber(taskB.timeslot?.startTime)
              ? allDayFirst
                ? -1
                : 1
              : 0
        : taskA.sortDate.getTime() - taskB.sortDate.getTime()
      : !!taskA.sortDate
        ? -1
        : 1;

export type DueDateOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
  invertedTimeslots?: boolean;
  allDayFirst?: boolean;
  expectedDueDate?: Timestamp;
  dateRange?: DateRange;
};

const getSortedTasksByDueDate = (
  tasks: Task[],
  {
    today,
    weekStartsOn,
    invertedTimeslots,
    allDayFirst,
    expectedDueDate,
    dateRange,
  }: DueDateOptions,
) => {
  const endOfExpectedDate = endOfDay(expectedDueDate ?? today);
  const { deadlineTasks, dueDateTasks } = tasks.reduce<{
    deadlineTasks: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    }[];
    dueDateTasks: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    }[];
  }>(
    (acc, task) => {
      if (dateRange && task.schedules) {
        const planning = getTaskPlanning(task, {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          today,
          weekStartsOn,
          sinceLastEntryOnly: true,
          uncompletedOnly: true,
        });

        const plannedOccurrence = planning[0];
        const dueDate =
          plannedOccurrence?.date ??
          getTaskDueDate(task, { today, weekStartsOn });

        const timeslot = plannedOccurrence
          ? plannedOccurrence.timeslot
          : task.timeslot;

        if (!dueDate && !task.deadlineDeadline) {
          acc.dueDateTasks.push({
            task,
            timeslot,
          });
          return acc;
        }

        if (dueDate) {
          acc.dueDateTasks.push({
            task,
            sortDate: endOfDay(dueDate),
            timeslot,
          });
          return acc;
        }

        if (task.deadlineDeadline) {
          acc.deadlineTasks.push({
            task,
            sortDate: endOfDay(task.deadlineDeadline),
            timeslot,
          });
        }

        return acc;
      }

      const planning = getDueTaskPlanning(task, {
        date: endOfExpectedDate,
        today,
        weekStartsOn,
      });
      const duePlanning = planning.filter(
        (plannedOccurrence) =>
          !isAfter(plannedOccurrence.date, endOfExpectedDate),
      );

      const plannedOccurrence = duePlanning[duePlanning.length - 1];
      const dueDate =
        plannedOccurrence?.date ??
        getTaskDueDate(task, { today, weekStartsOn });

      const timeslot = plannedOccurrence
        ? plannedOccurrence.timeslot
        : task.timeslot;

      if (!dueDate && !task.deadlineDeadline) {
        acc.dueDateTasks.push({
          task,
          timeslot,
        });
        return acc;
      }

      if (dueDate) {
        acc.dueDateTasks.push({
          task,
          sortDate: endOfDay(dueDate),
          timeslot,
        });
        return acc;
      }

      if (task.deadlineDeadline) {
        acc.deadlineTasks.push({
          task,
          sortDate: endOfDay(task.deadlineDeadline),
          timeslot,
        });
      }

      return acc;
    },
    { deadlineTasks: [], dueDateTasks: [] },
  );

  const sortFunc = taskSortGenerator({ invertedTimeslots, allDayFirst });

  return [...deadlineTasks.sort(sortFunc), ...dueDateTasks.sort(sortFunc)].map(
    ({ task }) => task,
  );
};

export type PriorityOptions = {
  direction?: OrderDirection;
};

const getSortedTasksByPriority = (
  tasks: Task[],
  { direction = OrderDirection.Asc }: PriorityOptions,
) =>
  tasks.toSorted((taskA, taskB) => {
    const directionTaskA = direction === OrderDirection.Asc ? taskA : taskB;
    const directionTaskB = direction === OrderDirection.Asc ? taskB : taskA;

    if (
      !directionTaskA.priority ||
      directionTaskA.priority === TaskPriorityOptions.None
    ) {
      return 1;
    }

    if (
      !directionTaskB.priority ||
      directionTaskB.priority === TaskPriorityOptions.None
    ) {
      return -1;
    }

    return (
      priorityRanking[directionTaskA.priority] -
      priorityRanking[directionTaskB.priority]
    );
  });

const getSortedTasksByCustom = (tasks: Task[], order: ID[]) =>
  tasks.toSorted((taskA, taskB) => {
    const indexA = order.indexOf(taskA.id);
    const indexB = order.indexOf(taskB.id);

    if (indexA === -1) {
      return 1;
    }
    if (indexB === -1) {
      return -1;
    }

    return indexA - indexB;
  });

export type Options = {
  today: Timestamp;
  weekStartsOn: WeekDays;
  direction?: OrderDirection;
  mode?: SortingMode[];
  order?: ID[];
  invertedTimeslots?: boolean;
  allDayFirst?: boolean;
  expectedDueDate?: Timestamp;
  dateRange?: DateRange;
};

export const getSortedTasks = (
  tasks: Task[],
  {
    today,
    weekStartsOn,
    direction,
    mode = [],
    order = [],
    invertedTimeslots,
    allDayFirst,
    expectedDueDate,
    dateRange,
  }: Options,
) => {
  let sortedTasks = tasks
    // first sort on createdAt to have a base ordering in case there are multiple tasks with same order values
    .toSorted(
      (taskA, taskB) => taskA.createdAt.getTime() - taskB.createdAt.getTime(),
    );

  mode?.forEach((mode) => {
    switch (mode) {
      case SortingMode.DueDate:
        sortedTasks = getSortedTasksByDueDate(sortedTasks, {
          today,
          weekStartsOn,
          invertedTimeslots,
          allDayFirst,
          expectedDueDate,
          dateRange,
        });
        break;
      case SortingMode.DeadlineDeadline:
        sortedTasks = sortedTasks.toSorted((taskA, taskB) => {
          if (!taskA.deadlineDeadline && !taskB.deadlineDeadline) {
            return 0;
          }
          if (!taskA.deadlineDeadline) {
            return 1;
          }
          if (!taskB.deadlineDeadline) {
            return -1;
          }
          return (
            taskA.deadlineDeadline.getTime() - taskB.deadlineDeadline.getTime()
          );
        });
        break;
      case SortingMode.Priority:
        sortedTasks = getSortedTasksByPriority(sortedTasks, { direction });
        break;
      case SortingMode.Name:
        sortedTasks = sortedTasks.toSorted((a, b) =>
          a.name.localeCompare(b.name),
        );
        break;
      case SortingMode.DeletedAt:
        sortedTasks = sortedTasks.toSorted((taskA, taskB) => {
          if (!taskA.deletedAt && !taskB.deletedAt) {
            return 0;
          }
          if (!taskA.deletedAt) {
            return 1;
          }
          if (!taskB.deletedAt) {
            return -1;
          }
          return taskB.deletedAt.getTime() - taskA.deletedAt.getTime();
        });
        break;
      case SortingMode.Custom:
        sortedTasks = getSortedTasksByCustom(sortedTasks, order);
        break;
    }
  });

  return sortedTasks;
};
